"use client";

import React, { useState, useTransition } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Input,
  Label,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  createTimeOffRequestAction,
  updateTimeOffRequestStatusAction,
  deleteTimeOffRequestAction,
} from "@/actions/capacity-actions";
import { Trash2, Check, X, ChevronLeft, ChevronRight } from "lucide-react";

interface TimeOffRequest {
  id: string;
  organizationId: string;
  agentId: string;
  startDate: Date;
  endDate: Date;
  type: string;
  status: string;
  reason: string | null;
  createdAt: Date;
  updatedAt: Date;
  agentName: string;
}

interface TimeOffClientProps {
  initialRequests: TimeOffRequest[];
  currentAgent: { id: string; name: string } | null;
}

export function TimeOffClient({
  initialRequests,
  currentAgent,
}: TimeOffClientProps) {
  const [requests, setRequests] = useState<TimeOffRequest[]>(initialRequests);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [type, setType] = useState("vacation");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [isPending, startTransition] = useTransition();

  // Calendar State
  const now = new Date();
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(now.getMonth()); // 0-11

  // Months name array
  const months = [
    "Gennaio",
    "Febbraio",
    "Marzo",
    "Aprile",
    "Maggio",
    "Giugno",
    "Luglio",
    "Agosto",
    "Settembre",
    "Ottobre",
    "Novembre",
    "Dicembre",
  ];

  // Calendar calculations
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  // Get day index of the 1st of the month, adjusted for Monday start (Monday=0, Sunday=6)
  const firstDayOfWeek =
    (new Date(currentYear, currentMonth, 1).getDay() + 6) % 7;

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleCreateRequest = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!startDate || !endDate) {
      setError("Inserire le date di inizio e fine.");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError(
        "La data di inizio non può essere successiva alla data di fine.",
      );
      return;
    }

    startTransition(async () => {
      const res = await createTimeOffRequestAction({
        startDate,
        endDate,
        type,
        reason,
      });

      if (res.error) {
        setError(res.error);
      } else {
        setSuccess("Richiesta di assenza inviata con successo!");
        setStartDate("");
        setEndDate("");
        setReason("");

        if (currentAgent) {
          const newReq: TimeOffRequest = {
            id: res.id || Math.random().toString(),
            organizationId: "",
            agentId: currentAgent.id,
            startDate: new Date(startDate),
            endDate: new Date(endDate),
            type,
            status: "pending",
            reason: reason || null,
            createdAt: new Date(),
            updatedAt: new Date(),
            agentName: currentAgent.name,
          };
          setRequests([newReq, ...requests]);
        }
      }
    });
  };

  const handleUpdateStatus = (
    requestId: string,
    status: "approved" | "rejected",
  ) => {
    startTransition(async () => {
      const res = await updateTimeOffRequestStatusAction({ requestId, status });
      if (res.error) {
        setError(res.error);
      } else {
        setRequests(
          requests.map((r) =>
            r.id === requestId ? { ...r, status, updatedAt: new Date() } : r,
          ),
        );
      }
    });
  };

  const handleDelete = (requestId: string) => {
    if (!confirm("Sei sicuro di voler eliminare questa richiesta?")) return;

    startTransition(async () => {
      const res = await deleteTimeOffRequestAction(requestId);
      if (res.error) {
        setError(res.error);
      } else {
        setRequests(requests.filter((r) => r.id !== requestId));
      }
    });
  };

  // Helper to format date
  const formatDateString = (d: Date) => {
    return new Date(d).toLocaleDateString("it-IT", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Col 1: Form e statistiche */}
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Richiedi Time Off</CardTitle>
            <CardDescription>
              Invia una richiesta di ferie, permesso o malattia.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateRequest} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="startDate">Data Inizio</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={isPending}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="endDate">Data Fine</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={isPending}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="type">Tipologia</Label>
                <Select
                  id="type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  disabled={isPending}
                >
                  <option value="vacation">Ferie (Vacation)</option>
                  <option value="sick">Malattia (Sick Leave)</option>
                  <option value="personal">Permesso Personale</option>
                  <option value="other">Altro</option>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="reason">Motivazione (Opzionale)</Label>
                <Input
                  id="reason"
                  placeholder="Es. Visita medica, Ferie estive..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={isPending}
                />
              </div>

              {error && <ErrorMessage>{error}</ErrorMessage>}
              {success && (
                <p className="text-sm text-green-600 font-medium">{success}</p>
              )}

              <Button type="submit" className="w-full mt-2" loading={isPending}>
                Invia Richiesta
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Col 2 & 3: Calendario ed Elenco Richieste */}
      <div className="lg:col-span-2 space-y-6">
        {/* Calendario */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <div>
              <CardTitle>Calendario Assenze del Team</CardTitle>
              <CardDescription>
                Visualizza le assenze approvate per ciascun giorno.
              </CardDescription>
            </div>
            <div className="flex items-center gap-1 border border-border rounded-lg p-1 bg-card">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={prevMonth}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-semibold px-2 min-w-[100px] text-center">
                {months[currentMonth]} {currentYear}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={nextMonth}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Calendar Grid Header */}
            <div className="grid grid-cols-7 gap-1 text-center font-semibold text-xs text-text-secondary mb-2">
              <div>Lun</div>
              <div>Mar</div>
              <div>Mer</div>
              <div>Gio</div>
              <div>Ven</div>
              <div>Sab</div>
              <div>Dom</div>
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {/* Empty cells before month starts */}
              {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  className="min-h-[75px] bg-card-muted/30 border border-border/20 rounded-md"
                />
              ))}

              {/* Days of the month */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const dayNum = i + 1;
                const dayDate = new Date(currentYear, currentMonth, dayNum);

                // Find approved requests overlapping this day
                const dayLeaves = requests.filter((r) => {
                  if (r.status !== "approved") return false;
                  const start = new Date(r.startDate);
                  const end = new Date(r.endDate);
                  start.setHours(0, 0, 0, 0);
                  end.setHours(23, 59, 59, 999);
                  return dayDate >= start && dayDate <= end;
                });

                return (
                  <div
                    key={`day-${dayNum}`}
                    className="min-h-[75px] bg-card border border-border/60 rounded-md p-1 flex flex-col justify-between hover:border-primary-300 transition-colors"
                  >
                    <span className="text-xs font-semibold text-text-secondary">
                      {dayNum}
                    </span>
                    <div className="space-y-1 overflow-y-auto max-h-[50px] scrollbar-none mt-1">
                      {dayLeaves.map((leaf) => {
                        let badgeColor =
                          "bg-primary-50 text-primary-700 border-primary-100";
                        if (leaf.type === "sick")
                          badgeColor =
                            "bg-error-50 text-error-700 border-error-100";
                        if (leaf.type === "personal")
                          badgeColor =
                            "bg-amber-50 text-amber-700 border-amber-100";

                        return (
                          <div
                            key={leaf.id}
                            title={`${leaf.agentName}: ${leaf.type} (${leaf.reason || "nessuna motivazione"})`}
                            className={`text-[9px] px-1 py-0.5 rounded border leading-tight truncate font-medium ${badgeColor}`}
                          >
                            {leaf.agentName}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Tabella Richieste */}
        <Card>
          <CardHeader>
            <CardTitle>Richieste Inviate</CardTitle>
            <CardDescription>
              Storico delle richieste e azioni di approvazione.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border bg-card-muted/50 text-xs font-semibold text-text-secondary uppercase">
                    <th className="px-6 py-3">Agente</th>
                    <th className="px-6 py-3">Periodo</th>
                    <th className="px-6 py-3">Tipologia</th>
                    <th className="px-6 py-3">Motivazione</th>
                    <th className="px-6 py-3 text-center">Stato</th>
                    <th className="px-6 py-3 text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {requests.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-8 text-center text-text-muted"
                      >
                        Nessuna richiesta di time-off presente.
                      </td>
                    </tr>
                  ) : (
                    requests.map((r) => {
                      let statusBadge =
                        "bg-amber-50 text-amber-700 border-amber-100";
                      if (r.status === "approved")
                        statusBadge =
                          "bg-green-50 text-green-700 border-green-100";
                      if (r.status === "rejected")
                        statusBadge =
                          "bg-error-50 text-error-700 border-error-100";

                      return (
                        <tr
                          key={r.id}
                          className="hover:bg-card-muted/20 transition-colors"
                        >
                          <td className="px-6 py-4 font-medium text-foreground">
                            {r.agentName}
                          </td>
                          <td className="px-6 py-4 text-xs text-text-secondary">
                            {formatDateString(r.startDate)} -{" "}
                            {formatDateString(r.endDate)}
                          </td>
                          <td className="px-6 py-4 capitalize text-xs">
                            {r.type === "vacation"
                              ? "Ferie"
                              : r.type === "sick"
                                ? "Malattia"
                                : r.type === "personal"
                                  ? "Permesso"
                                  : r.type}
                          </td>
                          <td
                            className="px-6 py-4 text-xs text-text-secondary max-w-[150px] truncate"
                            title={r.reason || ""}
                          >
                            {r.reason || "-"}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span
                              className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusBadge}`}
                            >
                              {r.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right space-x-1 whitespace-nowrap">
                            {r.status === "pending" && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 px-2 border-green-200 text-green-700 hover:bg-green-50"
                                  onClick={() =>
                                    handleUpdateStatus(r.id, "approved")
                                  }
                                  disabled={isPending}
                                >
                                  <Check className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 px-2 border-error-200 text-error-700 hover:bg-error-50"
                                  onClick={() =>
                                    handleUpdateStatus(r.id, "rejected")
                                  }
                                  disabled={isPending}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-text-muted hover:text-error"
                              onClick={() => handleDelete(r.id)}
                              disabled={isPending}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
